import Image from 'next/image';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import { FaDiscord } from 'react-icons/fa6';

import GameIcon from '@/assets/svg/game-icon';
import Button from '@/common/button';
import { GAMERS_DAYS_CAMPAIGNS } from '@/constants/campaigns/gamers-day';
import { useAuth } from '@/hooks/use-auth';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';

interface PromoSectionProps {
  pathImage: string;
  customTrial?: number | null;
  onModalSignUp?: (state: boolean) => void;
  isLoaderDone?: boolean;
  isDiscordTrial: boolean;
  daysTrial?: number;
}

interface DefineButtonProps {
  customTrial?: number | null;
  isDiscordTrial: boolean;
  handleButtonCustomTrial: () => void;
  loading: boolean;
  t: any;
  daysTrial?: number;
  isEnabledGamersDay: boolean;
}

function DefineButton({ customTrial, isDiscordTrial, handleButtonCustomTrial, loading, t, daysTrial, isEnabledGamersDay }: DefineButtonProps) {
  if (customTrial && !isDiscordTrial) {
    return (
      <div className='space-y-2'>
        <Button variant='primary' className='mt-5 py-4 transition-opacity hover:opacity-80' onClick={handleButtonCustomTrial} loading={loading}>
          {t('trial.button_start_free_trial')}
        </Button>
        <p className='text-base font-normal text-green-300'>{t('trial.no_credit_card')}</p>
      </div>
    );
  }

  if (isDiscordTrial) {
    return (
      <div className='space-y-2'>
        <Link className='click_days_free_discord mt-5 flex items-stretch justify-center gap-2 rounded-xl bg-[#5865F2] px-2.5 py-4 text-center text-sm font-bold uppercase text-white transition-opacity hover:opacity-80 mobile:w-fit mobile:px-10' href={process.env.NEXT_PUBLIC_DISCORD_URL_TRIAL ?? ''} target='_blank' rel='noopener noreferrer'>
          <span className='hidden mobile:flex'>
            <FaDiscord size={24} />
          </span>
          <Trans values={{ days: daysTrial }}>{t('trial.button_days_free_discord')}</Trans>
        </Link>
        <p className='text-base font-normal text-green-300'>{t('trial.no_credit_card')}</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2.5'>
      <a href='#form' className='lex mt-5 flex w-full items-center justify-center rounded-xl bg-green-300 py-4 text-center text-black transition-opacity hover:opacity-80 mobile:w-fit mobile:px-10'>
        <span className='font-bold uppercase'>{t('trial.button_start_free_trial')}</span>
      </a>
      {isEnabledGamersDay && <p className='text-base font-normal text-green-300'>{t('trial.no_credit_card')}</p>}
    </div>
  );
}

export default function PromoSection({ daysTrial, pathImage, customTrial, onModalSignUp, isLoaderDone, isDiscordTrial = false }: PromoSectionProps) {
  const { t } = useTranslation('components/trial/promo-section');
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();
  const { isEnabled: isEnabledGamersDay } = useFeatureToggle({ key: GAMERS_DAYS_CAMPAIGNS, autoFetch: true });

  useEffect(() => {
    if (isLoaderDone && loading) {
      setLoading(false);
    }
  }, [isLoaderDone, loading]);

  const handleButtonCustomTrial = useCallback(() => {
    if (!onModalSignUp) return;
    if (user) {
      setLoading(true);
    }
    onModalSignUp(true);
  }, [onModalSignUp, user]);

  return (
    <div className='flex items-center justify-center'>
      <div className='flex w-full max-w-[1400px] flex-col items-center gap-10 px-5 xl:flex-row'>
        <div className='w-full overflow-hidden rounded-xl ipad:max-w-[60%]'>
          <Image className='size-full object-cover object-center' alt='Screen from app' src={pathImage} width={659} height={386} />
        </div>
        <div className='flex max-w-lg flex-col gap-y-9'>
          <div className='flex flex-col gap-y-6 text-start'>
            <div className='flex flex-col gap-y-6 text-xl font-[700] leading-none text-zinc-300'>
              <GameIcon />
              <div className='text-xl uppercase text-[#565B63]'>{t('trial.unlimited')}</div>
            </div>
            <div className='flex flex-col gap-y-5'>
              <h2 className='text-5xl font-[700] leading-none text-white'>{t('trial.no_more_games')}</h2>
              <p className='text-base font-[500] leading-snug text-[#53535F]'>{t('trial.unlock')}</p>
            </div>
          </div>
          <DefineButton customTrial={customTrial} isDiscordTrial={isDiscordTrial} handleButtonCustomTrial={handleButtonCustomTrial} loading={loading} t={t} daysTrial={daysTrial} isEnabledGamersDay={isEnabledGamersDay} />
        </div>
      </div>
    </div>
  );
}
